import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Chip } from '@/components/ui/chip'
import { cn } from '@/lib/utils'
import type { ViolationLite } from '@/features/validation-bridge'
import { humanizeRuleId } from './humanize-rule-id'

interface ValidatorViolationsListProps {
  /** Blocking-first ordered rows from selectViolations. */
  violations: ViolationLite[]
  onAskCogo: (violation: ViolationLite) => void
  /** Developer persona: append the raw rule id under the humanized title (F15). */
  showTechnicalDetail?: boolean
}

const SEVERITY_BADGE: Record<ViolationLite['severity'], 'destructive' | 'warning' | 'secondary'> = {
  violation: 'destructive',
  warning: 'warning',
  info: 'secondary',
}

/**
 * Per-violation list for the Validator panel — one row per finding
 * (block / element / message / severity), each with a working Ask-cogo
 * remediation. Complements the rule-aggregated ValidatorChecksTable, which
 * only carries counts. On-demand Ask-cogo clicks here are uncapped; the
 * top-3 cap applies only to auto-spawned chat bubbles.
 */
export function ValidatorViolationsList({ violations, onAskCogo, showTechnicalDetail }: ValidatorViolationsListProps) {
  if (violations.length === 0) return null

  return (
    <Card className="mb-4 overflow-hidden p-0" data-testid="validator-violations-list">
      <div className="border-b border-border bg-muted px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Violations ({violations.length})
      </div>
      <ul>
        {violations.map((v, i) => (
          <li
            key={`${v.blockId}-${v.gate}-${v.ruleId}-${v.elementId}-${i}`}
            data-testid="validator-violation-row"
            className={cn('flex items-start gap-3 px-4 py-2.5 text-sm', i > 0 && 'border-t border-border')}
          >
            <Badge variant={SEVERITY_BADGE[v.severity]} className="mt-0.5 shrink-0">
              {v.severity}
            </Badge>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-medium">{v.blockName}</span>
                {v.elementId ? (
                  <span className="font-mono text-xs text-muted-foreground">{v.elementId}</span>
                ) : null}
                <Chip>{v.gate}</Chip>
              </div>
              <div className="mt-0.5">{v.message}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{humanizeRuleId(v.ruleId)}</div>
              {showTechnicalDetail && (
                <div
                  className="mt-0.5 font-mono text-xs text-muted-foreground"
                  data-testid="validator-violation-rule-id"
                >
                  {v.ruleId}
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid="validator-violation-ask-cogo"
              onClick={() => onAskCogo(v)}
              className="shrink-0 rounded bg-primary px-2 py-0.5 text-xs text-primary-foreground hover:bg-primary/90"
            >
              Review issue
            </button>
          </li>
        ))}
      </ul>
    </Card>
  )
}
